import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { dataExchangeApi } from "@/services/data-exchange-api.service";
import type { ImportBatchBrief, ImportMode, ConflictStrategy } from "@/types/api-v2";
import { Upload, RefreshCw, FileUp } from "lucide-react";
import ImportBatchStatusDialog from "./ImportBatchStatusDialog";

const STATUS_BADGE: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  parsing: "bg-blue-100 text-blue-800",
  validating: "bg-blue-100 text-blue-800",
  mapping: "bg-blue-100 text-blue-800",
  applying: "bg-blue-100 text-blue-800",
  committed: "bg-green-100 text-green-800",
  partial: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
  "dry-run-ready": "bg-teal-100 text-teal-800",
  conflicts: "bg-amber-100 text-amber-800",
};

export default function DataImportPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<ImportMode>("objects");
  const [strategy, setStrategy] = useState<ConflictStrategy>("strict");
  const [dryRun, setDryRun] = useState(true);
  const [password, setPassword] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);

  const { data: batches = [], isLoading } = useQuery({
    queryKey: ["import-batches"],
    queryFn: () => dataExchangeApi.listBatches({ limit: 50 }),
    refetchInterval: 5000,
  });

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setProgress(0);
    try {
      const result = await dataExchangeApi.uploadImport(Array.from(files), {
        mode,
        strategy,
        dryRun,
        password: password || undefined,
        onProgress: setProgress,
      });
      const first = Array.isArray(result) ? result[0] : result;
      toast({
        title: dryRun ? "Dry-run завершён" : "Импорт завершён",
        description: `Батч №${first.id}: статус ${first.status}`,
      });
      queryClient.invalidateQueries({ queryKey: ["import-batches"] });
      if (first.id) setSelectedBatchId(first.id);
    } catch (e: any) {
      toast({
        title: "Ошибка импорта",
        description: e?.message || "Не удалось обработать архив",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border p-4 space-y-4">
        <h3 className="text-md font-medium">Импорт данных</h3>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1">
            <Label>Режим</Label>
            <Select value={mode} onValueChange={(v) => setMode(v as ImportMode)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Режим" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="objects">Объекты + геометрии</SelectItem>
                <SelectItem value="template">Данные по шаблону</SelectItem>
                <SelectItem value="sync">Sync-пакет (партнёр)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Стратегия</Label>
            <Select value={strategy} onValueChange={(v) => setStrategy(v as ConflictStrategy)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Стратегия" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="strict">strict — стоп при ошибках</SelectItem>
                <SelectItem value="auto">auto — пропуск проблемных строк</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Пароль архива (опц.)</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Если ZIP зашифрован"
            />
          </div>
        </div>

        <div className="flex items-center gap-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Switch id="dry-run" checked={dryRun} onCheckedChange={setDryRun} />
            <Label htmlFor="dry-run">Режим проверки (dry-run)</Label>
          </div>

          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            {uploading ? "Загрузка…" : "Загрузить архив(ы) .zip"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>

        {uploading && <Progress value={progress} className="h-2" />}
        <p className="text-sm text-muted-foreground">
          Формат пакета — ZIP с manifest.json и листами xlsx. В dry-run выполняются только валидация и отчёт; для
          записи данных подтвердите импорт из окна статуса.
        </p>
      </div>

      <div className="rounded-lg border">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="text-md font-medium">Последние батчи</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: ["import-batches"] })}
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Файл</TableHead>
              <TableHead>Режим</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead>Dry-run</TableHead>
              <TableHead>Дата</TableHead>
              <TableHead className="text-right">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                  Загрузка…
                </TableCell>
              </TableRow>
            ) : batches.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                  Загрузок ещё не было
                </TableCell>
              </TableRow>
            ) : (
              batches.map((b: ImportBatchBrief) => (
                <TableRow key={b.id} className="cursor-pointer" onClick={() => setSelectedBatchId(b.id)}>
                  <TableCell>{b.id}</TableCell>
                  <TableCell className="max-w-xs truncate">{b.archive_name ?? "—"}</TableCell>
                  <TableCell>{b.mode}</TableCell>
                  <TableCell>
                    <Badge className={STATUS_BADGE[b.status] ?? ""}>{b.status}</Badge>
                  </TableCell>
                  <TableCell>{b.dry_run ? "да" : "нет"}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {new Date(b.created_at).toLocaleString("ru-RU")}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedBatchId(b.id);
                      }}
                    >
                      <FileUp className="mr-1 h-4 w-4" />
                      Открыть
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {selectedBatchId !== null && (
        <ImportBatchStatusDialog batchId={selectedBatchId} onClose={() => setSelectedBatchId(null)} />
      )}
    </div>
  );
}